import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useDeleteSelectionShortcut } from './useDeleteSelectionShortcut';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import {
  useArchiveUserCommunication,
  useMarkAsReadUserCommunication,
  useMarkAsUnreadUserCommunication,
  useReportAsSpamUserCommunication,
} from '../UserCommunications';
import { useCustomToast } from '../useCustomToast';

interface UseInboxSnoozedActionsProps {
  filteredCommunications: UserCommunication[];
  selectedItems: Set<string>;
  toggleItemSelection: (messageId: string) => void;
  clearSelection?: () => void;
}

function getSelectedEmail(
  filteredCommunications: UserCommunication[],
  selectedEmailIndex: number,
): UserCommunication | null {
  if (
    selectedEmailIndex >= 0 &&
    selectedEmailIndex < filteredCommunications.length
  ) {
    return filteredCommunications[selectedEmailIndex] ?? null;
  }
  return null;
}

export function useInboxSnoozedActions({
  filteredCommunications,
  selectedItems,
  toggleItemSelection,
  clearSelection,
}: UseInboxSnoozedActionsProps) {
  const { registerShortcut, unregisterShortcut, currentContext } =
    useKeyboardShortcuts();
  const { selectedEmailIndex } = useShortcutsContext();
  const { t } = useTranslation();
  const { promiseToast } = useCustomToast();

  const { handleArchiveUserCommunication } = useArchiveUserCommunication();
  const deleteHandler = useDeleteSelectionShortcut({
    filteredCommunications,
    selectedItems,
    selectedEmailIndex,
    clearSelection,
  });
  const { handleMarkAsUnreadUserCommunication } =
    useMarkAsUnreadUserCommunication();
  const { handleMarkAsReadUserCommunication } =
    useMarkAsReadUserCommunication();
  const { handleReportAsSpamUserCommunication } =
    useReportAsSpamUserCommunication();

  useEffect(() => {
    const ctx = [ShortcutContext.SNOOZED];
    unregisterShortcut(ShortcutKey.SELECT, ctx);
    unregisterShortcut(ShortcutKey.ARCHIVE, ctx);
    unregisterShortcut(ShortcutKey.ARCHIVE_ALT, ctx);
    unregisterShortcut(ShortcutKey.DELETE, ctx);
    unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
    unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
    unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
    unregisterShortcut(ShortcutKey.REPORT_SPAM, ctx);

    registerShortcut({
      key: ShortcutKey.SELECT,
      contexts: [ShortcutContext.SNOOZED],
      handler: () => {
        if (
          selectedEmailIndex >= 0 &&
          selectedEmailIndex < filteredCommunications.length
        ) {
          const selectedEmail = filteredCommunications[selectedEmailIndex];
          if (selectedEmail) {
            toggleItemSelection(selectedEmail.messageId);
          }
        }
      },
      description: 'Select/deselect current email',
    });

    const archiveHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleArchiveUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.archiving'),
          t('inbox.userCommunication.actions.archiveMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.archiveError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleArchiveUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.ARCHIVE,
      contexts: [ShortcutContext.SNOOZED],
      handler: archiveHandler,
      description: 'Archive selected email(s)',
    });

    registerShortcut({
      key: ShortcutKey.ARCHIVE_ALT,
      contexts: [ShortcutContext.SNOOZED],
      handler: archiveHandler,
      description: 'Archive selected email(s) (Gmail style)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE,
      contexts: [ShortcutContext.SNOOZED],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    registerShortcut({
      key: ShortcutKey.DELETE_KEY,
      contexts: [ShortcutContext.SNOOZED],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    registerShortcut({
      key: ShortcutKey.BACKSPACE,
      contexts: [ShortcutContext.SNOOZED],
      handler: deleteHandler,
      description: 'Delete selected email(s)',
    });

    const markAsReadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        promiseToast(
          handleMarkAsReadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsRead'),
          t('inbox.userCommunication.actions.markAsReadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsReadError'),
        );
      } else {
        const selectedEmail = getSelectedEmail(
          filteredCommunications,
          selectedEmailIndex,
        );
        if (selectedEmail) {
          handleMarkAsReadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_READ,
      contexts: [ShortcutContext.SNOOZED],
      handler: markAsReadHandler,
      description: 'Mark selected email(s) as read',
    });

    const markAsUnreadHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);

        promiseToast(
          handleMarkAsUnreadUserCommunication(itemsArray, clearSelection),
          t('inbox.userCommunication.actions.markingAsUnread'),
          t('inbox.userCommunication.actions.markAsUnreadMultipleSuccess', {
            count: selectedItems.size,
          }),
          t('inbox.userCommunication.actions.markAsUnreadError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleMarkAsUnreadUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.MARK_AS_UNREAD,
      contexts: [ShortcutContext.SNOOZED],
      handler: markAsUnreadHandler,
      description: 'Mark selected email(s) as unread',
      shiftKey: true,
    });

    const reportSpamHandler = () => {
      if (selectedItems.size > 0) {
        const itemsArray = Array.from(selectedItems);
        clearSelection?.();

        promiseToast(
          handleReportAsSpamUserCommunication(itemsArray),
          t('inbox.userCommunication.actions.reportingAsSpam'),
          t('inbox.userCommunication.actions.reportAsSpamMultipleSuccess', {
            count: itemsArray.length,
          }),
          t('inbox.userCommunication.actions.reportAsSpamError'),
        );
      } else if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          handleReportAsSpamUserCommunication([selectedEmail.messageId]);
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.REPORT_SPAM,
      contexts: [ShortcutContext.SNOOZED],
      handler: reportSpamHandler,
      description: 'Report selected email(s) as spam',
    });

    return () => {
      unregisterShortcut(ShortcutKey.SELECT, ctx);
      unregisterShortcut(ShortcutKey.ARCHIVE, ctx);
      unregisterShortcut(ShortcutKey.ARCHIVE_ALT, ctx);
      unregisterShortcut(ShortcutKey.DELETE, ctx);
      unregisterShortcut(ShortcutKey.DELETE_KEY, ctx);
      unregisterShortcut(ShortcutKey.BACKSPACE, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_READ, ctx);
      unregisterShortcut(ShortcutKey.MARK_AS_UNREAD, ctx);
      unregisterShortcut(ShortcutKey.REPORT_SPAM, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    selectedItems,
    toggleItemSelection,
    clearSelection,
    handleArchiveUserCommunication,
    deleteHandler,
    handleMarkAsUnreadUserCommunication,
    handleMarkAsReadUserCommunication,
    handleReportAsSpamUserCommunication,
    promiseToast,
    t,
    currentContext,
  ]);
}
